import React from "react";

import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { ServiceAccountOAuthClientInfo } from "src/clients/taktile-api";
import { toastActions } from "src/design-system/Toast/utils";
import { useDeleteOAuthClient } from "src/oauthClients/api/queries";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type Props = {
  clientToDelete?: ServiceAccountOAuthClientInfo;
  open: boolean;
  organizationId: string;
  workspaceId: string;
  onClose: () => void;
  afterLeave: () => void;
};

export const DeleteOAuthClientModal: React.FC<Props> = ({
  clientToDelete,
  open,
  organizationId: orgId,
  workspaceId: wsId,
  onClose,
  afterLeave,
}) => {
  const deleteOAuthClient = useDeleteOAuthClient(
    orgId,
    wsId,
    clientToDelete?.client_id ?? "",
  );

  const handleConfirm = async () => {
    if (!clientToDelete) return;

    try {
      await deleteOAuthClient.mutateAsync();
    } catch (error) {
      logger.error(`Error when deleting OAuth client: ${error}`);
      toastActions.failure({
        title: "Failed to delete the OAuth client",
        description: errorMessage(error),
      });
    }
    onClose();
  };

  const target = clientToDelete
    ? `"${clientToDelete.client_name}"`
    : "this OAuth client";

  return (
    <TextConfirmationModal
      afterLeave={afterLeave}
      challengeText={clientToDelete?.client_name}
      description={`Are you sure you'd like to delete ${target}? This is an irreversible action, and any integration signing with its keys will stop getting tokens.`}
      label="Type OAuth client name"
      open={open}
      title="Confirm Delete OAuth client"
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};
